import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../../ui/Button';
import CommonModal from '../../ui/CommonModal';
import { Domain } from '../../../types';
import { useDeleteDomain } from '../../../hooks/useApi';

interface DeleteDomainModalProps {
  domain: Domain | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteDomainModal: React.FC<DeleteDomainModalProps> = ({ domain, isOpen, onClose, onSuccess }) => {
  const deleteDomain = useDeleteDomain();

  if (!isOpen || !domain) return null;

  const handleDelete = async () => {
    try {
      await deleteDomain.mutateAsync(domain.id);
      onSuccess();
      onClose();
    } catch {
      // error handled by hook
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      closeDisabled={deleteDomain.isLoading}
      title={
        <>
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" aria-hidden />
          Delete Domain
        </>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={deleteDomain.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleteDomain.isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteDomain.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </>
      }
    >
      <p className="mb-2 text-sm text-muted-foreground">Are you sure you want to delete this domain?</p>
      <p className="mb-4 font-mono text-sm font-medium">{domain.domain}</p>
      <p className="text-xs text-destructive">
        This will immediately block all traffic from this domain. This action cannot be undone.
      </p>
    </CommonModal>
  );
};

export default DeleteDomainModal;
